import { Injector } from '@angular/core';
import { UIRouter } from '@uirouter/core';
import { IdentityService } from '../core/authentication';
import { LicenseType } from '../core/authentication/models/licenses';
import { AccountMode, LicenseService } from '../core/license.service';
import { SpacesService } from '../common/components/spaces';
import { getCurrentSpaceFromPath, getPathForSpace } from '../common/components/spaces/index';
import {
    getDefaultSpacetimeState,
    isStateAllowedForSpacetimeMode,
} from '../features/spacetime/configs/spacetime-view.config';

/**
 * WS5 #10 Stage B B6 (THE ATOMIC CUT): native replacement for the AngularJS transition
 * hooks that were registered via `APP_MODULE.run(...)` in angularjs.module.ts:186-191.
 *
 * Passed as the `config` fn of `UIRouterModule.forRoot({ states, deferIntercept, otherwise, config })`
 * — @uirouter/angular invokes it with the singleton UIRouter + the root Injector during router
 * bootstrap, i.e. SYNCHRONOUSLY before main.ts's deferred `setTimeout(startUIRouter)` runs
 * `urlService.listen()/sync()`. So all 7 `onBefore` guards are installed before the first
 * transition fires (the install-before-sync ordering the AngularJS version relied on, where the
 * hooks installed inside `upgrade.bootstrap(...)` before main.ts re-enabled URL interception).
 *
 * Faithful 1:1 port of the 6 hook classes
 * (account-v2/src/app/global/transition-hooks/*, deleted in B7):
 *  - REGISTRATION ORDER is behavior: authentication-check before force-password-change-check
 *    (both match `requiresAuth`); permissions(staff) before permissions(perms). Preserved here.
 *  - dead deps dropped (force-password's unused `$location`, space-type's unused `ngrxStore`).
 *  - `$location.path()` → `router.urlService.path()` (both return the path, dropping the query).
 *  - license-check keeps its distinct `transition.abort()` + `stateService.go('hub', params)`
 *    shape (NOT `.target()` like the others) and the INSTALL-TIME `isFliptoStaff` snapshot.
 */
export function configureRouterHooks(router: UIRouter, injector: Injector): void {
    const transitions = router.transitionService;
    const identity = injector.get(IdentityService);
    const spacesService = injector.get(SpacesService);
    const licenseService = injector.get(LicenseService);

    // 1. authentication-check: anonymous user → sign-in (preserving the originating path).
    transitions.onBefore({
        to: (state) => state.data && state.data.requiresAuth
    }, (transition) => {
        if (identity.isAnonymous()) {
            return transition.router.stateService.target('sign-in', { redirectTo: router.urlService.path() });
        }
    });

    // 2. force-password-change-check: authenticated user flagged for password change → recover-password.
    transitions.onBefore({
        to: (state) => state.data && state.data.requiresAuth
    }, (transition) => {
        if (!identity.isAnonymous() && identity.user.isForceChangePassword) {
            return transition.router.stateService.target('recover-password');
        }
    });

    // 3. permissions-check (staff): flip.to-staff-only state hit by a non-staff user → home.
    transitions.onBefore({
        to: (state) => state.data && state.data.isFliptoStaff
    }, (transition) => {
        if (!identity.isFliptoStaff()) return transition.router.stateService.target('home');
        return true;
    });

    // 4. permissions-check (permissions): missing required permission → home.
    transitions.onBefore({
        to: (state) => state.data && state.data.requiredPermissions
    }, (transition) => {
        const permissionsRequired = transition.to().data.requiredPermissions;
        return identity.hasPermission(permissionsRequired) ? true : transition.router.stateService.target('home');
    });

    // 5. space-type-check: state requiring a space type the current space doesn't match → home.
    transitions.onBefore({
        to: (state) => !!state.data && state.data.requiredSpaceType
    }, (transition) => {
        const spacesRequired = Array.isArray(transition.to().data.requiredSpaceType)
            ? transition.to().data.requiredSpaceType
            : [transition.to().data.requiredSpaceType];
        if (!spacesService.current || spacesRequired.indexOf(spacesService.current.type) === -1) {
            return transition.router.stateService.target('home');
        }
    });

    // 6. params-check: required param missing on the target → home.
    transitions.onBefore({
        to: (state) => state.data && state.data.requiresParams
    }, (transition) => {
        const params = transition.to().data.requiresParams;
        if (params.some((param) => !transition.params()[param])) {
            return transition.router.stateService.target('home');
        }
    });

    // 7. license-check: snapshot staff status ONCE at install time (parity with the ctor-time
    //    snapshot in license-check.ts:9), then abort + redirect to hub if the license is missing.
    const isFliptoStaff = identity.isFliptoStaff();
    transitions.onBefore({
        to: (state) => state.data && state.data.requiredLicense
    }, (transition) => {
        const licenseRequired = transition.to().data.requiredLicense;
        const forceForFlipto = transition.to().data.forceForFlipto;
        if (licenseService.checkLicense(licenseService.getCurrentSpaceLicenses(), licenseRequired) || (forceForFlipto && isFliptoStaff)) {
            return true;
        } else {
            transition.abort();
            transition.router.stateService.go('hub', transition.params());
        }
    });

    // 8. spacetime-mode / account-mode check (1.73.x, ported from master's license-check.ts
    //    second hook): restricted (non-Full) accounts are confined to their licensed Spacetime
    //    surface; SFMC-mode users can't leave the SFMC tab set. Staff and anonymous users exempt
    //    (live checks, NOT the install-time snapshot hook 7 uses — parity with master).
    const isSpaceScoped = (transition): boolean => {
        const params = transition.params();
        return !!(params && params.type && params.slug);
    };
    transitions.onBefore({}, (transition) => {
        if (identity.isAnonymous()) return;
        if (identity.isFliptoStaff()) return;

        const targetName: string = transition.to().name;
        const spacetimeMode = licenseService.getSpacetimeMode();
        const isSpacetimeTarget = targetName === 'spacetime' || targetName.startsWith('spacetime.');

        // If the user is in a restricted SpacetimeMode (e.g. SFMC), any spacetime sub-state
        // not allowed by the mode's tabs is redirected to the mode default.
        if (isSpacetimeTarget && !isStateAllowedForSpacetimeMode(targetName, spacetimeMode)) {
            transition.abort();
            transition.router.stateService.go(getDefaultSpacetimeState(spacetimeMode), transition.params());
            return;
        }

        // AccountMode restrictions — only for SpacetimeOnly (non-Full) account access.
        if (licenseService.getAccountMode() === AccountMode.Full) return;
        if (!isSpaceScoped(transition)) return;
        if (isSpacetimeTarget) return;

        transition.abort();
        if (!licenseService.checkCurrentSpaceLicense(LicenseType.Spacetime)) {
            transition.router.stateService.go('home', transition.params());
        } else {
            transition.router.stateService.go(getDefaultSpacetimeState(spacetimeMode), transition.params());
        }
    });

    // WS5 #10 Stage B B6: recover from a dead-end lazy future-state placeholder. A space-mismatched
    // deep link (e.g. /p/{property}/account-settings — a Company/Mixed-only state NOT registered for a
    // Property space) is caught by the greedy `hub.**` catch-all future state. Under the former hybrid,
    // AngularJS' $location serialized the transitions and hub.** always upgraded to the clean `hub`. Under
    // pure @uirouter/angular the post-lazy-load upgrade races the not-yet-committed placeholder transition,
    // so the transition can settle on the component-less `*.**` placeholder (blank). Valid routes always
    // upgrade away from `*.**`, so a SUCCESS whose final state still ends in `.**` is exactly that dead end:
    // redirect to the clean space overview (URL-based, space-type-agnostic), reproducing the frozen hybrid's
    // redirect-to-overview for these (gate-documented VACUOUS) space-mismatched routes.
    transitions.onSuccess({}, (transition) => {
        const toName = transition.to().name;
        if (toName.endsWith('.**')) {
            // Force the clean parent state BY NAME ('hub.**' → 'hub'). A URL rewrite is a no-op
            // here — the unresolved remainder was already dropped, so the URL is already the space
            // root (/p/{slug}) yet still matched by the greedy `*.**` placeholder. Going by state
            // name bypasses URL matching and activates the real, component-bearing parent state,
            // reproducing the hybrid's hub.** → hub upgrade.
            transition.router.stateService.go(toName.slice(0, -3), transition.params());
        }
    });
}

/**
 * The `otherwise` rule — native port of angularjs.module.ts:139-148.
 * No matching route: send to `home` if there's no space in the path, else rewrite the URL to the
 * space's default path (which re-triggers routing into that space). Returning a TargetState/URL
 * string is the idiomatic @uirouter/angular equivalent of the v1 imperative `$state.go`/`$location.path`.
 */
export function routerOtherwise(matchValue: unknown, url: unknown, router: UIRouter) {
    const space = getCurrentSpaceFromPath();
    if (!space) {
        return router.stateService.target('home');
    }
    return getPathForSpace(space);
}
